// Collisions et lancers de rayons sur des rectangles alignés aux axes.
import type { GameMap, Rect } from './map';

export const clamp = (v: number, a: number, b: number) => (v < a ? a : v > b ? b : v);

export function circleHitsRect(x: number, y: number, r: number, R: Rect): boolean {
  const px = clamp(x, R.x0, R.x1), py = clamp(y, R.y0, R.y1);
  return (x - px) * (x - px) + (y - py) * (y - py) < r * r;
}

// Grille spatiale (cases de 4 m) : chaque case connaît les rectangles qui la touchent
const CELL = 4;
interface Index { cols: number; rows: number; cells: Int32Array[]; stamp: Uint32Array; mark: number }
const indexes = new WeakMap<Rect[], Index>(); // par liste de murs : les copies de la carte (une par raid) le partagent
function indexOf(map: GameMap): Index {
  let ix = indexes.get(map.rects);
  if (ix) return ix;
  const cols = Math.ceil(map.width / CELL) + 1, rows = Math.ceil(map.height / CELL) + 1;
  const lists: number[][] = Array.from({ length: cols * rows }, () => []);
  map.rects.forEach((R, i) => {
    const cx0 = Math.max(0, Math.floor(R.x0 / CELL)), cx1 = Math.min(cols - 1, Math.floor(R.x1 / CELL));
    const cy0 = Math.max(0, Math.floor(R.y0 / CELL)), cy1 = Math.min(rows - 1, Math.floor(R.y1 / CELL));
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) lists[cy * cols + cx]!.push(i);
  });
  ix = { cols, rows, cells: lists.map(l => Int32Array.from(l)), stamp: new Uint32Array(map.rects.length), mark: 0 };
  indexes.set(map.rects, ix);
  return ix;
}

// lift : hauteur des pieds (en saut ou debout sur un objet) ; le couvert bas plus bas que ça ne gêne pas
export function circleBlocked(map: GameMap, x: number, y: number, r: number, lift = -1): boolean {
  if (x < r || y < r || x > map.width - r || y > map.height - r) return true;
  const ix = indexOf(map);
  const cx0 = Math.max(0, Math.floor((x - r) / CELL)), cx1 = Math.min(ix.cols - 1, Math.floor((x + r) / CELL));
  const cy0 = Math.max(0, Math.floor((y - r) / CELL)), cy1 = Math.min(ix.rows - 1, Math.floor((y + r) / CELL));
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) {
      for (const i of ix.cells[cy * ix.cols + cx]!) {
        const R = map.rects[i]!;
        if (R.door && map.open?.has(R.door)) continue; // porte ouverte
        if (lift >= 0 && R.kind === 'low' && (R.height ?? 1) <= lift) continue; // on passe au-dessus
        if (x + r < R.x0 || x - r > R.x1 || y + r < R.y0 || y - r > R.y1) continue;
        if (circleHitsRect(x, y, r, R)) return true;
      }
    }
  }
  return false;
}

// Déplacement avec glissement le long des obstacles (axes séparés, petits pas)
export function moveCircle(map: GameMap, x: number, y: number, dx: number, dy: number, r: number, lift = -1): [number, number] {
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dy) / (r * 0.5)));
  for (let i = 0; i < steps; i++) {
    const nx = x + dx / steps;
    if (!circleBlocked(map, nx, y, r, lift)) x = nx;
    const ny = y + dy / steps;
    if (!circleBlocked(map, x, ny, r, lift)) y = ny;
  }
  return [x, y];
}

// Hauteur du sol sous un cercle : le dessus du plus haut objet bas (caisse, voiture, muret…) qu'il touche
// et qui ne dépasse pas maxTop (on ne se retrouve pas d'un coup sur un objet plus haut que ses pieds)
export function groundAt(map: GameMap, x: number, y: number, r: number, maxTop: number): number {
  const ix = indexOf(map);
  const cx0 = Math.max(0, Math.floor((x - r) / CELL)), cx1 = Math.min(ix.cols - 1, Math.floor((x + r) / CELL));
  const cy0 = Math.max(0, Math.floor((y - r) / CELL)), cy1 = Math.min(ix.rows - 1, Math.floor((y + r) / CELL));
  let g = 0;
  for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) for (const i of ix.cells[cy * ix.cols + cx]!) {
    const R = map.rects[i]!;
    if (R.kind !== 'low' || R.look === 'chainlink') continue;
    const h = R.height ?? 1;
    if (h <= g || h > maxTop || h > MAX_STAND) continue;
    if (circleHitsRect(x, y, r * 0.6, R)) g = h; // il faut avoir au moins la moitié du pied dessus
  }
  return g;
}
const MAX_STAND = 1.6; // plus haut, on ne peut pas monter dessus (même en sautant)

// Distance d'entrée d'un rayon dans un rectangle (méthode des dalles), ou Infinity
export function rayRect(ox: number, oy: number, dx: number, dy: number, R: Rect, max: number): number {
  let t0 = 0, t1 = max;
  if (Math.abs(dx) < 1e-9) { if (ox < R.x0 || ox > R.x1) return Infinity; }
  else {
    let a = (R.x0 - ox) / dx, b = (R.x1 - ox) / dx;
    if (a > b) [a, b] = [b, a];
    t0 = Math.max(t0, a); t1 = Math.min(t1, b);
    if (t0 > t1) return Infinity;
  }
  if (Math.abs(dy) < 1e-9) { if (oy < R.y0 || oy > R.y1) return Infinity; }
  else {
    let a = (R.y0 - oy) / dy, b = (R.y1 - oy) / dy;
    if (a > b) [a, b] = [b, a];
    t0 = Math.max(t0, a); t1 = Math.min(t1, b);
    if (t0 > t1) return Infinity;
  }
  return t0;
}

// Entrée et sortie d'un rayon (infini) dans un rectangle
export function rayRectSpan(ox: number, oy: number, dx: number, dy: number, R: Rect): [number, number] {
  let t0 = -Infinity, t1 = Infinity;
  for (const [o, d, lo, hi] of [[ox, dx, R.x0, R.x1], [oy, dy, R.y0, R.y1]] as const) {
    if (Math.abs(d) < 1e-9) continue;
    let a = (lo - o) / d, b = (hi - o) / d;
    if (a > b) [a, b] = [b, a];
    t0 = Math.max(t0, a); t1 = Math.min(t1, b);
  }
  return [Math.max(0, t0), Math.max(0, t1)];
}

export interface RayHit { t: number; rect: Rect | null }

// Premier obstacle sur le rayon. `bullets` : le couvert bas arrête aussi la balle, sauf celui contre lequel
// le tireur est collé (il tire par-dessus).
// Parcours des cases traversées par le rayon (DDA) : on s'arrête dès qu'un obstacle est plus proche que la case suivante
export function raycast(map: GameMap, ox: number, oy: number, ang: number, max: number, bullets: boolean, nearLow = 1.1): RayHit {
  const dx = Math.cos(ang), dy = Math.sin(ang);
  const ix = indexOf(map);
  const mark = ++ix.mark;
  if (mark === 0xffffffff) { ix.stamp.fill(0); ix.mark = 1; }
  let best = max, rect: Rect | null = null;
  let cx = Math.floor(ox / CELL), cy = Math.floor(oy / CELL);
  const sx = dx > 0 ? 1 : -1, sy = dy > 0 ? 1 : -1;
  const tdx = Math.abs(dx) > 1e-9 ? CELL / Math.abs(dx) : Infinity, tdy = Math.abs(dy) > 1e-9 ? CELL / Math.abs(dy) : Infinity;
  let tmx = Math.abs(dx) > 1e-9 ? ((dx > 0 ? (cx + 1) * CELL - ox : ox - cx * CELL) / Math.abs(dx)) : Infinity;
  let tmy = Math.abs(dy) > 1e-9 ? ((dy > 0 ? (cy + 1) * CELL - oy : oy - cy * CELL) / Math.abs(dy)) : Infinity;
  for (let guard = 0; guard < 400; guard++) {
    if (cx >= 0 && cy >= 0 && cx < ix.cols && cy < ix.rows) {
      for (const i of ix.cells[cy * ix.cols + cx]!) {
        if (ix.stamp[i] === ix.mark) continue;
        ix.stamp[i] = ix.mark;
        const R = map.rects[i]!;
        if (R.door && map.open?.has(R.door)) continue;
        if (R.kind === 'low' && !bullets) continue;
        if (bullets && R.look === 'chainlink') continue; // grillage : les balles passent entre les mailles
        const t = rayRect(ox, oy, dx, dy, R, best);
        if (t >= best) continue;
        if (R.kind === 'low' && t < nearLow) continue;
        best = t;
        rect = R;
      }
    }
    const next = Math.min(tmx, tmy);
    if (next >= best) break;
    if (tmx < tmy) { cx += sx; tmx += tdx; } else { cy += sy; tmy += tdy; }
  }
  return { t: best, rect };
}

export function lineOfSight(map: GameMap, x0: number, y0: number, x1: number, y1: number): boolean {
  const d = Math.hypot(x1 - x0, y1 - y0);
  if (d < 0.01) return true;
  return raycast(map, x0, y0, Math.atan2(y1 - y0, x1 - x0), d, false).t >= d - 0.01;
}

// Distance le long du rayon à laquelle il touche un cercle, ou Infinity
export function rayCircle(ox: number, oy: number, dx: number, dy: number, cx: number, cy: number, r: number, max: number): number {
  const fx = cx - ox, fy = cy - oy;
  const t = fx * dx + fy * dy;
  if (t < 0 || t > max + r) return Infinity;
  const px = fx - dx * t, py = fy - dy * t;
  const d2 = px * px + py * py;
  if (d2 > r * r) return Infinity;
  const back = Math.sqrt(r * r - d2);
  const hit = t - back;
  return hit >= 0 && hit <= max ? hit : Infinity;
}

export const angleDiff = (a: number, b: number) => Math.abs(((a - b + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
